import { Card, CardAttachment, CardLog } from '@/models/card/CardModel';
import { client } from './API';
import { Comment } from '@/models/CommentModel';
import { ENVIRONMENTAL_VARIABLES } from '@/utils/Environment';

/**
 * Fetch list of cards of all steps
 */
export const fetchCards = ({
  communityId,
  roomId,
  boardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
}): Promise<Card[]> => {
  return client
    .get(`${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/questions`)
    .then((response) => response.data);
};

/**
 * Create card in initial step
 */
export const createCard = ({
  communityId,
  roomId,
  boardId,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  data: Card;
}): Promise<Card> => {
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/questions`,
      data,
    )
    .then((response) => response.data);
};

/**
 * Update card values (from/to/question/etc.)
 */
export const updateCard = ({
  communityId,
  roomId,
  boardId,
  cardId,
  stepId,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  stepId?: string;
  data: Card;
}): Promise<Card> => {
  return client
    .put(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/questions/${cardId}`,
      data,
      { params: stepId ? { step_id: stepId } : {} },
    )
    .then((response) => response.data);
};

/**
 * Lock card
 */
export const lockCard = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}): Promise<Card> => {
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/questions/${cardId}/lock`,
    )
    .then((response) => response.data);
};

/**
 * Unlock card
 */
export const unlockCard = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}): Promise<Card> => {
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/questions/${cardId}/unlock`,
    )
    .then((response) => response.data);
};

/**
 * Move card to a specific step
 */
export const moveCard = ({
  communityId,
  roomId,
  boardId,
  cardId,
  stepId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  stepId: string;
}): Promise<Card> => {
  return client
    .patch(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/questions/${cardId}`,
      {},
      { params: { step_id: stepId } },
    )
    .then((response) => response.data);
};

/**
 * Fetch card attachment infos
 */
export const fetchCardAttachments = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}): Promise<CardAttachment[]> => {
  return client
    .get(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/attachments`,
    )
    .then((response) => response.data);
};

/**
 * Upload attachment with content to card
 */
export const uploadCardAttachment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  file,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  file: File;
}): Promise<CardAttachment> => {
  const data = new FormData();
  data.append('file', file, file.name);
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/files`,
      data,
      { headers: { Accept: '*/*', 'Content-Type': 'multipart/form-data' } },
    )
    .then((response) => response.data);
};

/**
 * Add link to card
 */
export const addLink = ({
  communityId,
  roomId,
  boardId,
  cardId,
  link,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  link: any;
}): Promise<Card> => {
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/links`,
      link,
    )
    .then((response) => response.data);
};

/**
 * Delete attachment from card
 */
export const deleteCardAttachment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  attachmentId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  attachmentId: string;
}): Promise<Card> => {
  return client
    .delete(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/attachments/${attachmentId}`,
    )
    .then((response) => response.data);
};

export const downloadCardAttachment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  attachmentId,
  version,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  attachmentId: string;
  version: string;
}): Promise<Blob> => {
  const config = {
    responseType: 'blob', // important
    params: version ? { version } : {},
  };

  return client.get(
    `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/files/${attachmentId}`,
    config as any,
  );
};

/**
 * Fetch all card comments
 */
export const fetchCardComments = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}): Promise<Comment[]> => {
  return client
    .get(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/comments`,
    )
    .then((response) => response.data);
};

/**
 * Create comment in card
 */
export const createCardComment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  content,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  content: string;
}): Promise<Comment> => {
  const comment = { content };
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/comments`,
      comment,
    )
    .then((response) => response.data);
};

/**
 * Update comment in card
 */
export const updateCardComment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  comment,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  comment: Comment;
}): Promise<Comment> => {
  return client
    .put(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/comments/${comment.id}`,
      comment,
    )
    .then((response) => response.data);
};

/**
 * Delete comment in card
 */
export const deleteCardComment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  commentId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  commentId: string;
}): Promise<Comment> => {
  return client
    .delete(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/comments/${commentId}`,
    )
    .then((response) => response.data);
};

/**
 * Fetch card log
 */
export const fetchCardLog = async ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}): Promise<CardLog[]> => {
  return client
    .get(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${cardId}/logs`,
    )
    .then((response) => response.data);
};
